import React from 'react';

/* ─────────────────────────────────────────────────────────────────────────────
   Professional classroom illustration – teacher + two happy students
   Style: modern flat design with warm brand colours
───────────────────────────────────────────────────────────────────────────── */
export default function LearningIllustration() {
  return (
    <svg
      viewBox="0 0 600 380"
      xmlns="http://www.w3.org/2000/svg"
      style={{ width: '100%', maxWidth: '540px', display: 'block', margin: '0 auto' }}
      aria-label="Happy teacher with students in a classroom"
    >
      <defs>
        {/* Background gradient */}
        <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFF9D6" />
          <stop offset="50%" stopColor="#FFFEF5" />
          <stop offset="100%" stopColor="#FFE4EC" />
        </linearGradient>
        {/* Floor gradient */}
        <linearGradient id="floor" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#F0EBE3" />
          <stop offset="100%" stopColor="#E8DFD5" />
        </linearGradient>
        {/* Desk gradient */}
        <linearGradient id="desk" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#D4B896" />
          <stop offset="100%" stopColor="#B8956A" />
        </linearGradient>
        {/* Board gradient */}
        <linearGradient id="board" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#1a7a42" />
          <stop offset="100%" stopColor="#27A85A" />
        </linearGradient>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="4" stdDeviation="4" floodColor="rgba(0,0,0,0.10)" />
        </filter>
        <filter id="softShadow" x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="rgba(0,0,0,0.08)" />
        </filter>
      </defs>

      {/* ── Background ── */}
      <rect width="600" height="380" fill="url(#bg)" rx="20" />

      {/* ── Floor ── */}
      <rect x="0" y="300" width="600" height="80" fill="url(#floor)" rx="0" />
      <rect x="0" y="299" width="600" height="3" fill="#D9C9B8" />

      {/* ── Decorative bubbles (background) ── */}
      <circle cx="540" cy="60" r="50" fill="rgba(232,82,122,0.07)" />
      <circle cx="30" cy="320" r="40" fill="rgba(39,168,90,0.07)" />
      <circle cx="580" cy="340" r="30" fill="rgba(245,200,0,0.1)" />

      {/* ══════════════════════════════════════
          WHITEBOARD
      ══════════════════════════════════════ */}
      {/* Board frame */}
      <rect x="18" y="50" width="220" height="160" rx="6" fill="#5c4a3a" filter="url(#shadow)" />
      {/* Board surface */}
      <rect x="24" y="56" width="208" height="148" rx="4" fill="url(#board)" />
      {/* Board glare */}
      <rect x="26" y="58" width="80" height="4" rx="2" fill="rgba(255,255,255,0.12)" />

      {/* Board content – math equation */}
      <text x="55" y="98" fontSize="18" fill="#FFE55A" fontFamily="Georgia, serif" fontWeight="bold">2x + 5 = 13</text>
      {/* underline */}
      <line x1="55" y1="103" x2="175" y2="103" stroke="rgba(255,229,90,0.4)" strokeWidth="1.5" />
      {/* solution */}
      <text x="80" y="125" fontSize="15" fill="#90EEB0" fontFamily="Georgia, serif">x = 4 ✓</text>
      {/* ABC row */}
      <text x="42" y="152" fontSize="13" fill="rgba(255,255,255,0.7)" fontFamily="sans-serif" letterSpacing="6">A B C D E</text>
      {/* Smiley drawn on board */}
      <circle cx="186" cy="118" r="16" fill="none" stroke="rgba(255,255,255,0.35)" strokeWidth="2" />
      <path d="M178 120 Q186 128 194 120" stroke="rgba(255,255,255,0.35)" strokeWidth="2" fill="none" strokeLinecap="round" />
      <circle cx="181" cy="115" r="1.5" fill="rgba(255,255,255,0.4)" />
      <circle cx="191" cy="115" r="1.5" fill="rgba(255,255,255,0.4)" />

      {/* Board ledge */}
      <rect x="18" y="208" width="220" height="10" rx="3" fill="#5c4a3a" />
      {/* Chalk on ledge */}
      <rect x="80" y="210" width="18" height="5" rx="2" fill="#EEE8D5" />
      <rect x="104" y="210" width="12" height="5" rx="2" fill="#FFB3C1" />

      {/* ══════════════════════════════════════
          TEACHER (left, standing)
      ══════════════════════════════════════ */}
      {/* Floor shadow */}
      <ellipse cx="155" cy="305" rx="28" ry="8" fill="rgba(0,0,0,0.10)" />

      {/* Shoes */}
      <ellipse cx="143" cy="304" rx="14" ry="6" fill="#2C1B0E" />
      <ellipse cx="167" cy="304" rx="14" ry="6" fill="#2C1B0E" />

      {/* Legs */}
      <rect x="135" y="258" width="14" height="50" rx="7" fill="#5C3D7A" />
      <rect x="157" y="258" width="14" height="50" rx="7" fill="#5C3D7A" />

      {/* Skirt */}
      <path d="M126 232 Q155 245 184 232 L178 262 Q155 270 132 262 Z" fill="#9B59B6" />

      {/* Body / blouse */}
      <path d="M130 170 Q155 163 180 170 L184 235 Q155 242 126 235 Z" fill="#E8527A" />

      {/* Left arm (holding book) */}
      <path d="M130 185 Q112 205 108 222" stroke="#F5C5A3" strokeWidth="14" strokeLinecap="round" fill="none" />
      {/* Book */}
      <rect x="86" y="215" width="34" height="26" rx="4" fill="#F5C800" filter="url(#softShadow)" />
      <rect x="90" y="220" width="26" height="3" rx="1" fill="rgba(0,0,0,0.15)" />
      <rect x="90" y="226" width="26" height="3" rx="1" fill="rgba(0,0,0,0.15)" />
      <rect x="90" y="232" width="18" height="3" rx="1" fill="rgba(0,0,0,0.10)" />
      {/* Book spine */}
      <rect x="86" y="215" width="5" height="26" rx="2" fill="#D4A800" />

      {/* Right arm (pointing at board) */}
      <path d="M180 185 Q194 175 208 168" stroke="#F5C5A3" strokeWidth="14" strokeLinecap="round" fill="none" />
      {/* Pointing hand */}
      <circle cx="212" cy="166" r="8" fill="#F5C5A3" />
      {/* Pointer finger extended */}
      <rect x="210" y="153" width="5" height="16" rx="2.5" fill="#F5C5A3" />

      {/* Collar */}
      <path d="M142 170 L155 185 L168 170" fill="white" opacity="0.6" />

      {/* Neck */}
      <rect x="147" y="155" width="16" height="20" rx="6" fill="#F5C5A3" />

      {/* Head */}
      <circle cx="155" cy="135" r="34" fill="#F5C5A3" filter="url(#softShadow)" />

      {/* Hair – bun style */}
      <path d="M121 128 Q122 95 155 93 Q188 95 189 128 Q186 105 155 103 Q124 105 121 128 Z" fill="#3B2507" />
      {/* Bun */}
      <circle cx="155" cy="98" r="14" fill="#3B2507" />
      <circle cx="155" cy="96" r="8" fill="#5C3D1A" />
      {/* Side hair */}
      <path d="M189 128 Q194 142 188 158" stroke="#3B2507" strokeWidth="9" strokeLinecap="round" fill="none" />
      <path d="M121 128 Q116 142 122 158" stroke="#3B2507" strokeWidth="9" strokeLinecap="round" fill="none" />

      {/* Eyes – happy/warm */}
      <circle cx="143" cy="136" r="6" fill="white" />
      <circle cx="167" cy="136" r="6" fill="white" />
      <circle cx="144" cy="137" r="4" fill="#3B2507" />
      <circle cx="168" cy="137" r="4" fill="#3B2507" />
      {/* Eye shine */}
      <circle cx="146" cy="135" r="1.5" fill="white" />
      <circle cx="170" cy="135" r="1.5" fill="white" />
      {/* Happy eye crinkle */}
      <path d="M137 131 Q143 128 149 131" stroke="#9B7040" strokeWidth="1.5" fill="none" strokeLinecap="round" />
      <path d="M161 131 Q167 128 173 131" stroke="#9B7040" strokeWidth="1.5" fill="none" strokeLinecap="round" />

      {/* Nose */}
      <path d="M152 144 Q155 149 158 144" stroke="#D4956A" strokeWidth="1.5" fill="none" strokeLinecap="round" />

      {/* Smile – big, happy */}
      <path d="M141 153 Q155 165 169 153" stroke="#C0392B" strokeWidth="2.5" fill="rgba(255,200,200,0.3)" strokeLinecap="round" />

      {/* Rosy cheeks */}
      <circle cx="136" cy="149" r="8" fill="rgba(232,82,122,0.18)" />
      <circle cx="174" cy="149" r="8" fill="rgba(232,82,122,0.18)" />

      {/* Earring */}
      <circle cx="121" cy="142" r="4" fill="#F5C800" />
      <circle cx="189" cy="142" r="4" fill="#F5C800" />

      {/* ══════════════════════════════════════
          STUDENT 1 – Girl with braids (center)
      ══════════════════════════════════════ */}
      {/* Desk 1 */}
      <rect x="262" y="248" width="130" height="14" rx="5" fill="url(#desk)" filter="url(#softShadow)" />
      <rect x="272" y="262" width="10" height="44" rx="4" fill="#A07850" />
      <rect x="372" y="262" width="10" height="44" rx="4" fill="#A07850" />
      {/* Notebook on desk */}
      <rect x="278" y="232" width="58" height="40" rx="4" fill="white" filter="url(#softShadow)" />
      <rect x="278" y="232" width="8" height="40" rx="4" fill="#E8527A" />
      <line x1="292" y1="242" x2="330" y2="242" stroke="#DDD" strokeWidth="1.5" />
      <line x1="292" y1="250" x2="330" y2="250" stroke="#DDD" strokeWidth="1.5" />
      <line x1="292" y1="258" x2="326" y2="258" stroke="#DDD" strokeWidth="1.5" />
      {/* Pencil */}
      <rect x="337" y="238" width="5" height="28" rx="2" fill="#F5C800" transform="rotate(-12, 340, 252)" />
      <polygon points="337,264 342,264 339.5,270" fill="#F5C5A3" transform="rotate(-12, 340, 252)" />

      {/* Shadow */}
      <ellipse cx="325" cy="303" rx="22" ry="7" fill="rgba(0,0,0,0.09)" />

      {/* Legs (seated) */}
      <path d="M314 270 Q313 290 308 303" stroke="#27A85A" strokeWidth="12" strokeLinecap="round" fill="none" />
      <path d="M336 270 Q340 290 345 303" stroke="#27A85A" strokeWidth="12" strokeLinecap="round" fill="none" />

      {/* Body */}
      <path d="M300 215 Q325 208 350 215 L350 274 Q325 280 300 274 Z" fill="#27A85A" />

      {/* Left arm on desk */}
      <path d="M300 225 Q286 242 282 252" stroke="#F4C2A1" strokeWidth="12" strokeLinecap="round" fill="none" />
      {/* Left hand */}
      <circle cx="280" cy="255" r="8" fill="#F4C2A1" />

      {/* Right arm raised / writing */}
      <path d="M350 225 Q360 238 363 248" stroke="#F4C2A1" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="365" cy="251" r="8" fill="#F4C2A1" />

      {/* Collar */}
      <path d="M314 215 L325 228 L336 215" fill="white" opacity="0.5" />

      {/* Neck */}
      <rect x="316" y="200" width="14" height="18" rx="5" fill="#F4C2A1" />

      {/* Head */}
      <circle cx="323" cy="183" r="28" fill="#F4C2A1" filter="url(#softShadow)" />

      {/* Hair – two braids/pigtails */}
      <path d="M295 176 Q296 152 323 150 Q350 152 351 176 Q348 158 323 156 Q298 158 295 176 Z" fill="#1A0D00" />
      {/* Left braid */}
      <path d="M295 176 Q290 188 292 202" stroke="#1A0D00" strokeWidth="9" strokeLinecap="round" fill="none" />
      <circle cx="291" cy="205" r="5" fill="#E8527A" />
      {/* Right braid */}
      <path d="M351 176 Q356 188 354 202" stroke="#1A0D00" strokeWidth="9" strokeLinecap="round" fill="none" />
      <circle cx="355" cy="205" r="5" fill="#E8527A" />

      {/* Eyes – wide and happy */}
      <circle cx="313" cy="183" r="5.5" fill="white" />
      <circle cx="333" cy="183" r="5.5" fill="white" />
      <circle cx="314" cy="184" r="3.5" fill="#1A0D00" />
      <circle cx="334" cy="184" r="3.5" fill="#1A0D00" />
      <circle cx="315.5" cy="182.5" r="1.5" fill="white" />
      <circle cx="335.5" cy="182.5" r="1.5" fill="white" />

      {/* Eyebrows */}
      <path d="M307 177 Q313 174 319 177" stroke="#1A0D00" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M327 177 Q333 174 339 177" stroke="#1A0D00" strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* Nose */}
      <circle cx="323" cy="190" r="1.5" fill="#D4956A" />

      {/* Big smile */}
      <path d="M311 197 Q323 208 335 197" stroke="#C0392B" strokeWidth="2.5" fill="rgba(255,200,200,0.4)" strokeLinecap="round" />

      {/* Rosy cheeks */}
      <circle cx="308" cy="193" r="7" fill="rgba(232,82,122,0.20)" />
      <circle cx="337" cy="193" r="7" fill="rgba(232,82,122,0.20)" />

      {/* ══════════════════════════════════════
          STUDENT 2 – Boy with glasses (right)
      ══════════════════════════════════════ */}
      {/* Desk 2 */}
      <rect x="410" y="243" width="130" height="14" rx="5" fill="url(#desk)" filter="url(#softShadow)" />
      <rect x="420" y="257" width="10" height="49" rx="4" fill="#A07850" />
      <rect x="520" y="257" width="10" height="49" rx="4" fill="#A07850" />
      {/* Laptop on desk */}
      <rect x="418" y="220" width="62" height="38" rx="4" fill="#2D2D2D" filter="url(#softShadow)" />
      <rect x="422" y="223" width="54" height="32" rx="2" fill="#3B5BDB" />
      {/* Laptop screen content */}
      <rect x="425" y="227" width="40" height="3" rx="1" fill="rgba(255,255,255,0.5)" />
      <rect x="425" y="233" width="30" height="3" rx="1" fill="rgba(255,255,255,0.35)" />
      <rect x="425" y="239" width="35" height="3" rx="1" fill="rgba(255,255,255,0.35)" />
      {/* Laptop hinge */}
      <rect x="414" y="257" width="70" height="6" rx="2" fill="#1A1A1A" />

      {/* Shadow */}
      <ellipse cx="466" cy="303" rx="22" ry="7" fill="rgba(0,0,0,0.09)" />

      {/* Legs (seated) */}
      <path d="M455 268 Q453 288 448 303" stroke="#3B5BDB" strokeWidth="12" strokeLinecap="round" fill="none" />
      <path d="M477 268 Q480 288 486 303" stroke="#3B5BDB" strokeWidth="12" strokeLinecap="round" fill="none" />

      {/* Body */}
      <path d="M442 210 Q466 203 490 210 L490 272 Q466 278 442 272 Z" fill="#3B5BDB" />

      {/* Left arm on desk/laptop */}
      <path d="M442 220 Q428 238 422 250" stroke="#FDBCB4" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="420" cy="253" r="8" fill="#FDBCB4" />

      {/* Right arm – raised, hand up answering! */}
      <path d="M490 215 Q500 196 503 178" stroke="#FDBCB4" strokeWidth="12" strokeLinecap="round" fill="none" />
      <circle cx="505" cy="174" r="9" fill="#FDBCB4" />
      {/* Raised finger */}
      <rect x="502" y="159" width="6" height="18" rx="3" fill="#FDBCB4" />

      {/* Collar */}
      <path d="M455 210 L466 224 L477 210" fill="white" opacity="0.5" />

      {/* Neck */}
      <rect x="458" y="195" width="14" height="18" rx="5" fill="#FDBCB4" />

      {/* Head */}
      <circle cx="465" cy="177" r="28" fill="#FDBCB4" filter="url(#softShadow)" />

      {/* Hair – short, curly top */}
      <path d="M437 168 Q438 145 465 143 Q492 145 493 168 Q490 150 465 148 Q440 150 437 168 Z" fill="#5C3D1A" />
      {/* Curly texture */}
      <path d="M442 160 Q448 154 454 160" stroke="#7A5230" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M456 157 Q462 151 468 157" stroke="#7A5230" strokeWidth="2.5" fill="none" strokeLinecap="round" />
      <path d="M470 159 Q476 153 482 159" stroke="#7A5230" strokeWidth="2.5" fill="none" strokeLinecap="round" />

      {/* Glasses */}
      <rect x="448" y="172" width="14" height="11" rx="4" fill="none" stroke="#2D2D2D" strokeWidth="2" />
      <rect x="467" y="172" width="14" height="11" rx="4" fill="none" stroke="#2D2D2D" strokeWidth="2" />
      <line x1="462" y1="177" x2="467" y2="177" stroke="#2D2D2D" strokeWidth="2" />
      <line x1="448" y1="177" x2="445" y2="175" stroke="#2D2D2D" strokeWidth="1.5" />
      <line x1="481" y1="177" x2="484" y2="175" stroke="#2D2D2D" strokeWidth="1.5" />

      {/* Eyes behind glasses */}
      <circle cx="455" cy="177" r="3.5" fill="#3B2507" />
      <circle cx="474" cy="177" r="3.5" fill="#3B2507" />
      <circle cx="456.5" cy="176" r="1.5" fill="white" />
      <circle cx="475.5" cy="176" r="1.5" fill="white" />

      {/* Eyebrows */}
      <path d="M449 169 Q455 166 461 169" stroke="#5C3D1A" strokeWidth="2" fill="none" strokeLinecap="round" />
      <path d="M467 169 Q473 166 479 169" stroke="#5C3D1A" strokeWidth="2" fill="none" strokeLinecap="round" />

      {/* Nose */}
      <path d="M462 186 Q465 191 468 186" stroke="#D4956A" strokeWidth="1.5" fill="none" strokeLinecap="round" />

      {/* Big open smile */}
      <path d="M452 194 Q465 206 478 194" stroke="#C0392B" strokeWidth="2.5" fill="rgba(255,200,200,0.4)" strokeLinecap="round" />

      {/* Rosy cheeks */}
      <circle cx="449" cy="190" r="7" fill="rgba(232,82,122,0.18)" />
      <circle cx="480" cy="190" r="7" fill="rgba(232,82,122,0.18)" />

      {/* ══════════════════════════════════════
          FLOATING DECORATIONS
      ══════════════════════════════════════ */}
      {/* Star sparkles */}
      <g opacity="0.9">
        {/* 4-point star */}
        <path d="M255 75 L258 83 L266 86 L258 89 L255 97 L252 89 L244 86 L252 83 Z" fill="#F5C800" />
      </g>
      <g opacity="0.7">
        <path d="M535 185 L537 191 L543 193 L537 195 L535 201 L533 195 L527 193 L533 191 Z" fill="#F5C800" />
      </g>
      {/* Small dots */}
      <circle cx="240" cy="110" r="4" fill="rgba(232,82,122,0.5)" />
      <circle cx="560" cy="230" r="5" fill="rgba(39,168,90,0.5)" />
      <circle cx="250" cy="290" r="3" fill="rgba(245,200,0,0.6)" />
      <circle cx="555" cy="95" r="3" fill="rgba(232,82,122,0.4)" />

      {/* Apple on board ledge */}
      <g transform="translate(38, 208)">
        <circle cx="0" cy="0" r="8" fill="#E8527A" />
        <path d="M0 -8 Q2 -12 0 -14" stroke="#27A85A" strokeWidth="2" fill="none" strokeLinecap="round" />
        <path d="M0 -8 Q-4 -10 -2 -7" fill="#27A85A" />
        <circle cx="-3" cy="-1" r="2" fill="rgba(255,255,255,0.3)" />
      </g>

      {/* Speech bubble from student 2 */}
      <g transform="translate(512, 130)">
        <rect x="-22" y="-18" width="44" height="30" rx="10" fill="white" filter="url(#softShadow)" />
        <polygon points="-5,12 5,12 -8,22" fill="white" />
        <text x="0" y="-2" fontSize="16" textAnchor="middle" dominantBaseline="middle">🙋</text>
      </g>

      {/* LittleNest brand tag at bottom */}
      <g transform="translate(300, 358)">
        <text
          textAnchor="middle"
          fontSize="11"
          fill="rgba(0,0,0,0.25)"
          fontFamily="'Nunito', sans-serif"
          fontWeight="600"
          letterSpacing="0.5"
        >
          LittleNest · Where learning feels like home
        </text>
      </g>
    </svg>
  );
}
